import { Fragment, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Divider, Typography } from '@mui/material';
import { SelectDropdownInput } from 'components/Inputs/Dropdown/SelectDropdown/SelectDropdownInput';
import { DateRangeDropdownInput } from 'components/Inputs/Dropdown/DateRangeDropdownInput';
import { getSelectSx } from 'components/Inputs/Dropdown/dropdownConsts';
import {
  getPopoverGridInputLabelSx,
  resetFiltersButtonSx,
} from 'components/Search/styles';
import { useLogSearch } from './LogSearchProvider';
import { clampDateRangeToMaxDays, getLogLevelChoices } from './logSearchConstants';

/**
 * Log search filter inputs. The date range is day-only and clamped to the
 * two-week cap via `clampDateRangeToMaxDays`.
 */

/** Active-filter chips row rendered below the message search bar. */
export const LogSearchFilterFields = () => {
  const { t } = useTranslation();
  const {
    filters,
    showAdvFilters,
    advFilterCount,
    updateFilterValue,
    clearFilters,
    keptKeys,
  } = useLogSearch();

  const levelChoices = useMemo(() => getLogLevelChoices(t), [t]);

  const handleResetFilters = useCallback(() => {
    clearFilters(keptKeys);
  }, [clearFilters, keptKeys]);

  return (
    <Fragment>
      <SelectDropdownInput
        staticChoices={levelChoices}
        inputLabel={t('blox.logs.level')}
        value={filters.level || []}
        updateValue={(_event, value) => updateFilterValue('level', value)}
        infiniteScroll={false}
        showFilter={false}
        showFooter={false}
        showChipBox={false}
        hidden={!showAdvFilters.showLevel}
        multiple
        selectSx={getSelectSx}
      />

      <DateRangeDropdownInput
        inputLabel={t('blox.logs.date_range')}
        value={[filters.start_date || '', filters.end_date || '']}
        updateValue={(_event, value) => {
          const [start, end] = clampDateRangeToMaxDays(value);
          updateFilterValue('start_date', start);
          updateFilterValue('end_date', end);
        }}
        applyChangesOnPopoverClose
        hidden={!showAdvFilters.showDate}
        timePicker={false}
      />

      {advFilterCount > 0 && (
        <Fragment>
          <Divider
            orientation="vertical"
            flexItem
          />
          <Button
            data-testid="log-search-reset-filters-button"
            onClick={handleResetFilters}
            variant="text"
            size="small"
            color="info"
            sx={resetFiltersButtonSx}
          >
            {t('blox.actions.reset_filters')}
          </Button>
        </Fragment>
      )}
    </Fragment>
  );
};

/** Advanced-filters popover body */
export const LogSearchPopoverFields = () => {
  const { t } = useTranslation();
  const { stagedFilters, updateStagedFilterValue } = useLogSearch();
  const levelChoices = useMemo(() => getLogLevelChoices(t), [t]);

  return (
    <Fragment>
      <Typography sx={getPopoverGridInputLabelSx}>{t('blox.logs.level')}</Typography>
      <SelectDropdownInput
        staticChoices={levelChoices}
        inputLabel={t('blox.logs.level')}
        value={stagedFilters.level || []}
        updateValue={(_event, value) => updateStagedFilterValue('level', value)}
        showFilter={false}
        showFooter={false}
        showChipBox={false}
        popoverMatchesFormControlWidth
        renderValueDisplay="chips"
        formControlVariant="outlined"
        inputPlaceholderText="stringify-options"
        multiple
        selectSx={getSelectSx}
      />

      <Typography sx={getPopoverGridInputLabelSx}>{t('blox.logs.date_range')}</Typography>
      <DateRangeDropdownInput
        inputLabel={t('blox.logs.date_range')}
        value={[stagedFilters.start_date || '', stagedFilters.end_date || '']}
        updateValue={(_event, value) => {
          const [start, end] = clampDateRangeToMaxDays(value);
          updateStagedFilterValue('start_date', start);
          updateStagedFilterValue('end_date', end);
        }}
        applyChangesOnPopoverClose
        renderValueDisplay="chips"
        formControlVariant="outlined"
        timePicker={false}
      />
    </Fragment>
  );
};
